import { useRef } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { subheadingParent, supportedText } from "@/animation/textAnimation";
import LucideIcon from "@/utils/LucideIcon";
import { testimonials } from "@/data/testimonials";
import { ITestimonialCardProps } from "@/Interfaces/interface.informationalPages";

export const TestimonialCard: React.FC<ITestimonialCardProps> = ({ testimonial }) => {
  const renderStars = (rating: number) =>
    Array.from({ length: 5 }, (_, i) => (
      <svg
        key={i}
        className={`w-3 h-3 ${i < rating ? "text-yellow-400" : "text-gray-300"}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));

  return (
    <div className="min-w-[280px] mx-3 bg-gray-50 rounded-xl shadow-lg p-5 flex flex-col justify-between transition-shadow duration-300 border border-gray-100 h-48 will-change-transform">
      <div className="mb-3">
        <div className="flex mb-2">{renderStars(testimonial.rating)}</div>
        <p className="text-gray-700 text-xs leading-relaxed line-clamp-3 font-body">
          "{testimonial.text}"
        </p>
      </div>
      <div className="flex items-center">
        <div
          className={`w-10 h-10 rounded-full bg-gradient-to-r ${testimonial.color} flex items-center justify-center text-white font-bold text-xs mr-3 shadow-md`}
        >
          {testimonial.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-xs truncate font-body">
            {testimonial.name}
          </p>
          <p className="text-xs text-gray-500 truncate font-body">{testimonial.position}</p>
          <p className="text-xs text-gray-400 truncate font-body">{testimonial.company}</p>
        </div>
      </div>
    </div>
  );
};

export default function TestimonialSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const half = Math.ceil(testimonials.length / 2);
  const topTestimonials = testimonials.slice(0, half);
  const bottomTestimonials = testimonials.slice(half);

  return (
    <section className="mx-auto">
      <div ref={sectionRef} className="relative min-h-screen w-full overflow-hidden">
        <div className="relative w-full z-10 px-4 container mx-auto">
          <motion.div className="text-center md:text-start">
            <motion.h2 {...subheadingParent}>
              <div className="flex items-center md:text-left justify-center md:justify-start mb-4">
                <LucideIcon
                  name="Route"
                  className="w-10 h-10 text-blue-900 hover:text-blue-900 hidden md:inline-block mr-3"
                />
                <h2 className="text-4xl md:text-5xl font-bold font-inter text-secondary-gradient bg-clip-text text-transparent pb-1">
                  What Our Clients Say !
                </h2>
              </div>
            </motion.h2>
            <motion.div
              className="w-24 h-1 bg-secondary-gradient mx-auto md:mx-0 md:ml-14 mb-8 rounded-xl"
              initial={{ width: 0 }}
              whileInView={{ width: 96 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ amount: 0.2 }}
            ></motion.div>
            <motion.p
              {...supportedText}
              className="text-xl text-gray-600 max-w-3xl leading-relaxed font-inter"
            >
              Trusted by businesses across India for exceptional customization services
            </motion.p>
          </motion.div>
        </div>

        {/* Scrolling Rows */}
        <div className="relative flex flex-col gap-4 md:gap-2 justify-center items-center py-4">
          {/* Top Row */}
          <div className="overflow-hidden flex items-center w-full">
            <motion.div
              className="flex flex-nowrap py-2"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ repeat: Infinity, duration: 20, ease: "linear" as const }}
            >
              {[...topTestimonials, ...topTestimonials].map((testimonial, idx) => (
                <TestimonialCard key={testimonial.id + "-" + idx} testimonial={testimonial} />
              ))}
            </motion.div>
          </div>

          {/* Bottom Row */}
          <div className="overflow-hidden flex items-center w-full">
            <motion.div
              className="flex flex-nowrap py-2"
              animate={{ x: ["-50%", "0%"] }}
              transition={{ repeat: Infinity, duration: 25, ease: "linear" as const }}
            >
              {[...bottomTestimonials, ...bottomTestimonials].map((testimonial, idx) => (
                <TestimonialCard key={testimonial.id + "-" + idx} testimonial={testimonial} />
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
